/**
 * Build the JSON-LD schemas we inject into <Helmet>:
 *   - LocalBusiness     (one per site)
 *   - Service           (one per service in config.services)
 *   - FAQPage           (only when config.faq is non-empty)
 *   - BreadcrumbList    (Home for now; multi-page templates can extend)
 *
 * Returns an array of plain objects ready to JSON.stringify.
 */
export function buildSeoSchemas(config) {
  const out = [];
  const url = config.seo?.canonical || (typeof window !== 'undefined' ? window.location.origin + '/' : '');
  const phone = config.phone_number || '';

  // LocalBusiness
  out.push({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: config.business_name,
    description: config.seo?.description || config.hero?.subhead || '',
    url,
    telephone: phone,
    image: config.seo?.og_image || undefined,
    address: config.city
      ? {
          '@type': 'PostalAddress',
          addressLocality: config.city.split(',')[0]?.trim() || config.city,
          addressRegion: (config.city.split(',')[1] || '').trim() || undefined,
          addressCountry: 'US',
        }
      : undefined,
    areaServed: Array.isArray(config.service_areas) && config.service_areas.length
      ? config.service_areas.map((c) => ({ '@type': 'City', name: c }))
      : config.service_area || undefined,
    foundingDate: config.founded_year ? String(config.founded_year) : undefined,
    openingHours: config.hours ? config.hours.replace('–', '-') : undefined,
    priceRange: '$$',
  });

  // Service entries
  if (Array.isArray(config.services)) {
    for (const s of config.services) {
      out.push({
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: s.title,
        provider: { '@type': 'LocalBusiness', name: config.business_name, telephone: phone },
        areaServed: config.service_area || (config.service_areas || []).join(', '),
        description: s.description,
      });
    }
  }

  // FAQPage
  if (Array.isArray(config.faq) && config.faq.length > 0) {
    out.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: config.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  // BreadcrumbList (Home only for single-page)
  out.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: url },
    ],
  });

  return out;
}
